import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import useImageCap from '@/hooks/useImageCap';
import OptimizedImage, { 
    getImageSrcset, 
    getBestAvailableUrl, 
    getDefaultSrc, 
    buildSrcsetString 
} from './OptimizedImage';

/**
 * Normalize crop data to 0-1 format
 * Accepts crop values in either 0-1 or 0-100 format (as stored in DB)
 * @param {Object} crop - Crop data { x, y, width, height }
 * @returns {Object|null} Normalized crop in 0-1 format, or null if invalid
 */
export function normalizeCrop(crop) {
    if (!crop || typeof crop !== 'object') return null;
    
    const { x, y, width, height } = crop;
    
    // Check if all required fields exist and are numbers
    if (typeof x !== 'number' || typeof y !== 'number' || 
        typeof width !== 'number' || typeof height !== 'number') {
        return null;
    }
    
    // Detect if values are in 0-100 format (any value > 1 indicates percentage)
    const isPercentage = x > 1 || y > 1 || width > 1 || height > 1;
    
    if (isPercentage) {
        return {
            x: x / 100,
            y: y / 100,
            width: width / 100,
            height: height / 100,
            ratio: crop.ratio, // Preserve ratio if present
        };
    }
    
    // Already in 0-1 format
    return { ...crop };
}

/**
 * Calculate CSS styles to apply a crop to an image
 * Uses an img element with transforms inside a masking container
 * @param {Object} crop - Crop data { x, y, width, height } (accepts 0-1 or 0-100)
 * @param {Object} [dimensions] - Original image dimensions { width, height }. When provided, scale
 *   uses Math.min when crop orientation differs from image orientation, Math.max when same.
 * @returns {Object} Style objects for wrapper and image
 */
export function getCropStyles(crop, dimensions = null) {
    const normalized = normalizeCrop(crop);
    if (!normalized) return { wrapper: {}, image: {} };

    // The container has overflow: hidden and acts as a mask
    // The image is scaled and translated to show only the crop region

    // Scale the image so the crop region fills the container.
    // Same orientation (both portrait, both landscape, or both square): use Math.max (crop fills container).
    // Different orientation, or square crop on non-square image (cropRatio === 1): use Math.min
    //   so the crop fits; otherwise translateY is wrong for vertical images.
    let scale;
    if (dimensions?.width && dimensions?.height) {
        const landscapeImage = dimensions.width > dimensions.height;
        scale = landscapeImage
            ? Math.max(1 / normalized.width, 1 / normalized.height)
            : Math.min(1 / normalized.width, 1 / normalized.height);
    } else {
        scale = Math.max(1 / normalized.width, 1 / normalized.height);
    }

    // Position the image so the crop region is visible.
    // Translate percentages are relative to the element's size (CSS transform).
    const translateX = -normalized.x * 100;
    const translateY = -normalized.y * 100;
    
    return {
        wrapper: {
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
        },
        image: {
            position: 'absolute',
            top: 0,
            left: 0,
            // Width only — height must stay intrinsic. Forcing height:100%
            // (e.g. via h-full) stretches non-square sources inside a crop box.
            width: '100%',
            height: 'auto',
            maxWidth: 'none',
            transform: `scale(${scale}) translate(${translateX}%, ${translateY}%)`,
            transformOrigin: 'top left',
        },
    };
}

/**
 * Calculate the aspect ratio for a cropped image
 * @param {Object} dimensions - Original image dimensions { width, height }
 * @param {Object} crop - Crop data { x, y, width, height } (accepts 0-1 or 0-100)
 * @returns {string} CSS aspect-ratio value
 */
export function getCroppedAspectRatio(dimensions, crop) {
    if (!dimensions?.width || !dimensions?.height) {
        return '1 / 1';
    }
    
    const normalized = normalizeCrop(crop);
    if (!normalized) {
        return `${dimensions.width} / ${dimensions.height}`;
    }
    
    const croppedWidth = dimensions.width * normalized.width;
    const croppedHeight = dimensions.height * normalized.height;
    
    return `${croppedWidth} / ${croppedHeight}`;
}

/**
 * Get optimized URLs for an image, accounting for crop scale factor
 * @param {Object} file - File object from database
 * @param {Object} crop - Optional crop data { x, y, width, height } (accepts 0-1 or 0-100)
 * @param {number} typicalDisplayWidth - Expected display width (default: 600)
 * @param {number} [maxWidth] - Widest rung to request (slow links)
 * @returns {Object} { src, srcset, srcsetString, sizes }
 */
export function getImageUrlsForCrop(file, crop = null, typicalDisplayWidth = 600, maxWidth = undefined) {
    if (!file) return { src: '', srcset: null, srcsetString: '', sizes: '' };
    
    const normalized = normalizeCrop(crop);
    const hasCrop = !!normalized;
    
    let src;
    let srcset = null;
    let srcsetString = '';
    
    if (hasCrop) {
        // For cropped images, calculate the scale factor and get a higher resolution source
        const scaleFactor = Math.max(1 / normalized.width, 1 / normalized.height);
        const neededSourceWidth = typicalDisplayWidth * scaleFactor;
        
        // Choose target size based on scale factor and needed width
        const wanted = scaleFactor > 1.5 ? 1920 :
            neededSourceWidth >= 1280 ? 1920 :
            neededSourceWidth >= 768 ? 1280 : 768;
        const targetSize = Number.isFinite(maxWidth) ? Math.min(wanted, maxWidth) : wanted;
        
        src = getBestAvailableUrl(file, targetSize) || getDefaultSrc(file);
        // For cropped images, we use a single high-res src instead of srcset
        // This ensures we get the calculated high resolution image
    } else {
        // No crop: use default src and full srcset
        src = Number.isFinite(maxWidth)
            ? (getBestAvailableUrl(file, Math.min(1280, maxWidth)) || getDefaultSrc(file))
            : getDefaultSrc(file);
        srcset = getImageSrcset(file);
        srcsetString = srcset ? buildSrcsetString(srcset, file.dimensions?.width, maxWidth) : '';
    }
    
    const sizes = '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw';
    
    return { src, srcset, srcsetString, sizes };
}

/**
 * CroppedImage Component
 * 
 * Renders an image with optional cropping support.
 * Accepts crop data in either 0-1 or 0-100 format and normalizes internally.
 * 
 * - No crop: Renders full image using OptimizedImage (with fill) or plain img
 * - With crop: Renders wrapper + img with CSS transforms to show crop region
 * 
 * Component assumes it lives in a sized container (parent sets aspect-ratio
 * via getCroppedAspectRatio). It always fills that container.
 */
const CroppedImage = forwardRef(function CroppedImage({
    image,
    crop = null,
    alt = '',
    sizes,
    priority = false,
    fill = true,
    className = '',
    style = {},
    onLoad,
    onError,
    cacheVersion,
    loading,
    draggable = false,
    ...rest
}, ref) {
    const imageCap = useImageCap();
    if (!image) return null;
    
    const normalized = normalizeCrop(crop);
    const hasCrop = !!normalized;
    
    // Get URLs based on crop
    const { src, srcset, srcsetString } = getImageUrlsForCrop(image, crop, 600, imageCap);
    
    if (!src) return null;
    
    // Normalize src to ensure leading slash
    let normalizedSrc = src?.startsWith('/') ? src : `/${src}`;
    
    // Add cache busting if cacheVersion provided
    if (cacheVersion) {
        const separator = normalizedSrc.includes('?') ? '&' : '?';
        normalizedSrc = `${normalizedSrc}${separator}v=${cacheVersion}`;
    }
    
    // Also add cache busting to srcset if present
    let finalSrcsetString = srcsetString;
    if (cacheVersion && srcsetString) {
        finalSrcsetString = srcsetString.replace(/(\S+)\s+(\d+w)/g, (match, url, width) => {
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}v=${cacheVersion} ${width}`;
        });
    }

    // Cache-bust the srcset object too (used by fill mode via OptimizedImage).
    // Without this, the browser renders a non-busted srcset candidate and can keep
    // showing a stale/failed cached response after an image is replaced.
    let finalSrcset = srcset;
    if (cacheVersion && srcset) {
        finalSrcset = {};
        for (const [width, url] of Object.entries(srcset)) {
            if (!url) continue;
            const separator = url.includes('?') ? '&' : '?';
            finalSrcset[width] = `${url}${separator}v=${cacheVersion}`;
        }
    }
    
    // Determine loading attribute
    const loadingAttr = loading !== undefined ? loading : (priority ? 'eager' : 'lazy');
    
    if (hasCrop) {
        // With crop: wrapper is the mask; img keeps its intrinsic ratio and is
        // scaled/translated. className goes on the wrapper so callers can fade
        // the tile without setting h-full on the img (which stretches it).
        const cropStyles = getCropStyles(normalized, image?.dimensions);

        return (
            <div className={className} style={cropStyles.wrapper}>
                <img
                    ref={ref}
                    src={normalizedSrc}
                    alt={alt}
                    draggable={draggable}
                    loading={loadingAttr}
                    decoding="async"
                    style={{ ...cropStyles.image, ...style }}
                    onLoad={onLoad}
                    onError={onError}
                    {...rest}
                />
            </div>
        );
    }
    
    // No crop: render full image
    if (fill) {
        // Use OptimizedImage for fill mode
        return (
            <OptimizedImage
                ref={ref}
                src={normalizedSrc}
                srcset={finalSrcset}
                originalWidth={image.dimensions?.width}
                alt={alt}
                fill
                sizes={sizes}
                priority={priority}
                className={className}
                style={style}
                onLoad={onLoad}
                {...rest}
            />
        );
    }
    
    // Non-fill mode: plain img with srcset
    return (
        <img
            ref={ref}
            src={normalizedSrc}
            srcSet={finalSrcsetString || undefined}
            sizes={finalSrcsetString ? sizes : undefined}
            alt={alt}
            draggable={draggable}
            loading={loadingAttr}
            decoding="async"
            className={className}
            style={style}
            onLoad={onLoad}
            onError={onError}
            {...rest}
        />
    );
});

CroppedImage.propTypes = {
    /** File object from database with url and dimensions */
    image: PropTypes.shape({
        url: PropTypes.object,
        dimensions: PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number,
        }),
    }),
    /** Crop data { x, y, width, height } - accepts 0-1 or 0-100 format */
    crop: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
        width: PropTypes.number,
        height: PropTypes.number,
    }),
    /** Alt text for accessibility */
    alt: PropTypes.string,
    /** Sizes attribute for responsive images */
    sizes: PropTypes.string,
    /** Load image eagerly (default: lazy) */
    priority: PropTypes.bool,
    /** Fill mode - image fills container (default: true) */
    fill: PropTypes.bool,
    /** Additional CSS classes */
    className: PropTypes.string,
    /** Additional inline styles */
    style: PropTypes.object,
    /** Callback when image loads */
    onLoad: PropTypes.func,
    /** Callback when image fails to load */
    onError: PropTypes.func,
    /** Cache version for cache busting */
    cacheVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Override loading attribute */
    loading: PropTypes.oneOf(['eager', 'lazy']),
    /** Whether image is draggable */
    draggable: PropTypes.bool,
};

export default CroppedImage;
